import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import {
  postDwellingOccupant,
  patchDwellingOccupant,
  postRoomOccupant,
  patchRoomOccupant,
  postBedOccupant,
  patchBedOccupant,
} from "../../services/httpClient";
import {
  MAX_FAMILY_ROOM_BEDS,
  bedDisplayName,
  sortBedsByName,
  propertyAddressLine,
  pluralizeCount,
  pluralizePeople,
  roomDisplayName,
} from "../../helpers/housingAssignment";
import PageTitle from "./PageTitle";
import useApp from "../../utils/useApp";
import SearchBar from "./SearchBar";
import BedRow from "./BedRow";
import InlineConfirm from "./InlineConfirm";
import RoomBedsPanel, { ManageBedsButton } from "./RoomBedsPanel";

interface CaseHousingFlowState {
  caseId: any;
  caseName: string;
  people: any[];
  dwellings: any[];
  facilities: any[];
  rooms: any[];
  beds: any[];
  backLabel: string;
  onClose: any;
  onChanged: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

export const personKey = (p: any) => `${p.type}-${p.id}`;
export const personName = (p: any) => `${p.preferredname ? p.preferredname : p.firstname} ${p.lastname ?? ""}`.trim();

// Housing a whole case: pick the shape, pick the place. Shared by the Household tab and the
// Shelter tab's needs-housing list so the two never drift apart.
const CaseHousingFlow = ({
  caseId,
  caseName,
  people,
  dwellings,
  facilities,
  rooms,
  beds,
  backLabel,
  onClose,
  onChanged,
  setShowToast,
  setToastText,
  setToastBody,
}: CaseHousingFlowState) => {
  const { showMenu }: any = useApp();
  const residentialPeople = people.filter((p: any) => p.is_residential === 1);

  const [step, setStep] = useState<string>("");
  const [mode, setMode] = useState<string>("");
  const [currentKey, setCurrentKey] = useState<string>("");
  const [openRoomId, setOpenRoomId] = useState<any>(null);
  const [invSearch, setInvSearch] = useState("");
  const [bedSearch, setBedSearch] = useState("");
  const [pendingMove, setPendingMove] = useState<any>(null);
  const [confirmVacateWhole, setConfirmVacateWhole] = useState<boolean>(false);
  const [confirmVacateKey, setConfirmVacateKey] = useState<string>("");
  const [errMsg, setErrMsg] = useState("");

  // Once the heading block pins itself to the top, the roster shrinks to whoever is being
  // placed - the whole list is worth seeing while it is in flow, not while it is in the way.
  const stickySentinel = useRef<HTMLDivElement>(null);
  const [headingStuck, setHeadingStuck] = useState(false);

  const numericCaseId = Number(caseId);

  const onError = (err: any) => {
    setErrMsg(err?.response?.data?.msg || "Could not save that housing change");
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const afterChange = (text: string, body: string) => {
    setShowToast(true);
    setToastText(text);
    setToastBody(body);
    setErrMsg("");
    setPendingMove(null);
    onChanged();
  };

  const bedsInRoom = (roomId: any) => sortBedsByName(beds.filter((b: any) => b.room_id === roomId));
  const roomsOfProperty = (propertyId: any) => rooms.filter((r: any) => r.property_id === propertyId);
  const dwellingBedCount = (d: any) => beds.filter((b: any) => roomsOfProperty(d.id).some((r: any) => r.id === b.room_id)).length;
  // A facility's room reads "Facility 2 · Room 3". A standalone room has no facility to name,
  // so it would sit in the same list as a bare "Room 9" and read like an incomplete label -
  // name its kind where the others name their building.
  const roomLabel = (room: any) =>
    room?.property_id ? roomDisplayName(room, facilities) : [room?.name && "Standalone", room?.name].filter(Boolean).join(" · ");

  // How this case is housed right now, read straight off the places rather than re-asking
  // the server: a dwelling or a room carries the case id, and individual beds carry it too.
  const housedDwelling = dwellings.find((d: any) => Number(d.occupant_case_id) === numericCaseId);
  const housedRoom = rooms.find((r: any) => Number(r.family_case_id) === numericCaseId);

  const bedByPerson = useMemo(() => {
    const map = new Map<string, any>();
    beds.forEach((b: any) => {
      if (b.occupant_id) map.set(`${b.occupant_type}-${b.occupant_id}`, b);
    });
    return map;
  }, [beds]);

  const placedPeople = residentialPeople.filter((p: any) => bedByPerson.has(personKey(p)));
  const currentMode = housedDwelling ? "dwelling" : housedRoom ? "room" : placedPeople.length > 0 ? "individual" : "";
  const activeMode = mode || currentMode || "dwelling";

  // the sentinel only exists in bed mode, so re-attach whenever that view comes back
  useEffect(() => {
    const sentinel = stickySentinel.current;
    if (activeMode !== "individual" || !sentinel || typeof IntersectionObserver === "undefined") {
      setHeadingStuck(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setHeadingStuck(!entry.isIntersecting), {
      rootMargin: `-${showMenu ? 180 : 80}px 0px 0px 0px`,
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showMenu, activeMode]);

  const hasAnyHousing = !!housedDwelling || !!housedRoom || placedPeople.length > 0;

  // Opening on an established shape skips the chooser; a case with nothing yet starts there.
  const activeStep = step || (currentMode ? "mode" : "choose");

  const modeName = (m: string) => (m === "dwelling" ? "Dwelling" : m === "room" ? "Room" : "Bed");

  // where the case sits right now, for the move confirmation
  const currentPlaceLabel = housedDwelling ? housedDwelling.name : housedRoom ? roomLabel(housedRoom) : "";

  // Skip moves along to whoever is still waiting, wrapping so the last one hands back round.
  const nextWaitingAfter = (person: any) => {
    const waiting = residentialPeople.filter((p: any) => !bedByPerson.has(personKey(p)));
    if (waiting.length <= 1) return personKey(person);
    const at = waiting.findIndex((p: any) => personKey(p) === personKey(person));
    return personKey(waiting[(at + 1) % waiting.length]);
  };

  const onPickMode = (picked: string) => {
    setMode(picked);
    setPendingMove(null);
    setStep("mode");
  };

  // ---- whole-case options, pre-filtered to what actually holds everyone ----

  // Tightest fit first: three people should be offered the three-bed dwelling before the
  // nine-bed one, so the big places stay available for the big cases. Bed count is decorated
  // on before sorting rather than recomputed inside the comparator, which would walk every
  // room and bed on each comparison.
  const dwellingOptions = useMemo(
    () =>
      dwellings
        .filter((d: any) => !d.occupant_case_id || Number(d.occupant_case_id) === numericCaseId)
        .map((d: any) => ({ dwelling: d, bedCount: dwellingBedCount(d) }))
        .filter((o: any) => o.bedCount >= residentialPeople.length)
        .sort((a: any, b: any) => a.bedCount - b.bedCount || a.dwelling.name.localeCompare(b.dwelling.name, undefined, { numeric: true }))
        .map((o: any) => o.dwelling),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dwellings, rooms, beds, residentialPeople.length],
  );

  // A room inside a dwelling is never assigned on its own - the dwelling goes as one - so
  // only a facility's rooms and standalone rooms can be handed to a case.
  const isAssignableRoom = (r: any) => !r.property_id || facilities.some((f: any) => f.id === r.property_id);

  // Must count the same beds the inventory below will actually offer. A bed in a dwelling is
  // only ever handed over as part of the whole dwelling, and the empty beds in a room assigned
  // to a family belong to that case - neither carries an occupant_id, so counting on that
  // alone advertises beds this screen will never list.
  const openBedCount = useMemo(() => {
    const offerableRoomIds = new Set(rooms.filter((r: any) => isAssignableRoom(r) && !r.family_case_id).map((r: any) => r.id));
    return beds.filter((b: any) => !b.occupant_id && offerableRoomIds.has(b.room_id)).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms, beds, facilities]);

  // Tightest fit first, same as the dwellings above. The room the case already holds stays in
  // the list whatever its size, so it can still be seen and vacated.
  const roomOptions = useMemo(
    () =>
      rooms
        .filter(isAssignableRoom)
        .map((r: any) => ({ room: r, bedCount: bedsInRoom(r.id).length }))
        .filter(({ room: r }: any) => {
          if (Number(r.family_case_id) === numericCaseId) return true;
          if (r.family_case_id) return false;
          const roomBeds = bedsInRoom(r.id);
          if (roomBeds.some((b: any) => b.occupant_id)) return false;
          // the API refuses to hand a dormitory to one case, so don't offer it
          return roomBeds.length >= residentialPeople.length && roomBeds.length <= MAX_FAMILY_ROOM_BEDS;
        })
        .sort(
          (a: any, b: any) => a.bedCount - b.bedCount || roomLabel(a.room).localeCompare(roomLabel(b.room), undefined, { numeric: true }),
        )
        .map((o: any) => o.room),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rooms, beds, facilities, residentialPeople.length],
  );

  // ---- bed-by-bed inventory, grouped the way the shelter is laid out ----

  // Breaking a case out of a whole room into individual beds almost always keeps them in the
  // same building, so that facility leads the list instead of sorting alphabetically into it.
  // Read once when the flow opens: recomputing it would hoist a facility to the top of the
  // list the moment the first person lands there, moving everything under the user mid-task.
  const [homeFacilityId] = useState(() => {
    if (housedRoom?.property_id) return housedRoom.property_id;
    const anyPlaced = residentialPeople.map((p: any) => bedByPerson.get(personKey(p))).find(Boolean);
    const placedRoom = anyPlaced ? rooms.find((r: any) => r.id === anyPlaced.room_id) : undefined;
    return placedRoom?.property_id || null;
  });

  const inventory = useMemo(() => {
    const q = invSearch.trim().toLowerCase();
    const entries: any[] = [];

    const pushRooms = (label: string, list: any[], note?: string) => {
      const withSpace = list
        .filter((r: any) => !r.family_case_id)
        .map((r: any) => {
          const roomBeds = bedsInRoom(r.id);
          return { room: r, roomBeds, open: roomBeds.filter((b: any) => !b.occupant_id) };
        })
        .filter((r: any) => r.open.length > 0)
        .filter((r: any) => !q || roomLabel(r.room).toLowerCase().includes(q))
        .sort((a: any, b: any) => a.room.name.localeCompare(b.room.name, undefined, { numeric: true }));
      if (withSpace.length === 0) return;
      entries.push({ kind: "group", label, note });
      withSpace.forEach((r: any) => entries.push({ kind: "room", ...r }));
    };

    facilities
      .slice()
      .sort((a: any, b: any) => {
        if (a.id === homeFacilityId) return -1;
        if (b.id === homeFacilityId) return 1;
        return a.name.localeCompare(b.name, undefined, { numeric: true });
      })
      .forEach((f: any) => pushRooms(f.name, roomsOfProperty(f.id), ""));
    pushRooms(
      "Standalone Rooms",
      rooms.filter((r: any) => !r.property_id),
    );

    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilities, rooms, beds, invSearch, homeFacilityId]);

  // whoever is being placed right now - the first person still without a bed unless picked
  const fallbackPerson = residentialPeople.find((p: any) => !bedByPerson.has(personKey(p)));
  const currentPerson = currentKey ? residentialPeople.find((p: any) => personKey(p) === currentKey) || null : fallbackPerson || null;

  // pinned to the top of the window the roster is competing with the inventory for room, so it
  // drops to whoever is being placed - plus anyone mid-confirmation, whose buttons must not vanish
  const rosterPeople =
    headingStuck && currentPerson && residentialPeople.length > 1
      ? residentialPeople.filter(
          (p: any) => personKey(p) === personKey(currentPerson) || (confirmVacateKey && personKey(p) === confirmVacateKey),
        )
      : residentialPeople;

  // ---- actions ----

  const endAllBeds = () =>
    residentialPeople
      .map((p: any) => bedByPerson.get(personKey(p)))
      .filter(Boolean)
      .reduce((chain: Promise<any>, bed: any) => chain.then(() => patchBedOccupant(bed.case_id, bed.bed_occupant_id)), Promise.resolve());

  // Whatever shape the case is in now has to be undone before it can take another one.
  const endCurrentHousing = () => {
    if (housedDwelling) return patchDwellingOccupant(caseId, housedDwelling.dwelling_occupant_id);
    if (housedRoom) return patchRoomOccupant(caseId, housedRoom.bed_room_occupant_id);
    return endAllBeds();
  };

  // Ending the old assignment and taking the new one are two calls with nothing transactional
  // between them, and the second one fails for perfectly ordinary reasons - a colleague taking
  // the place first, or somebody being marked residential in between. Without putting the old
  // assignment back, a routine rejection leaves a real family recorded as sleeping nowhere.
  const priorHousing = () => ({
    dwelling: housedDwelling,
    room: housedRoom,
    beds: residentialPeople.map((p: any) => bedByPerson.get(personKey(p))).filter(Boolean),
  });

  const restoreHousing = (prior: any): Promise<any> => {
    if (prior.dwelling) return postDwellingOccupant({ property_id: prior.dwelling.id }, caseId);
    if (prior.room) return postRoomOccupant({ room_id: prior.room.id }, caseId);
    return prior.beds.reduce(
      (chain: Promise<any>, bed: any) =>
        chain.then(() =>
          postBedOccupant(
            bed.occupant_type === "guardian"
              ? { bed_id: bed.id, user_id: bed.occupant_id }
              : { bed_id: bed.id, member_id: bed.occupant_id },
            caseId,
          ),
        ),
      Promise.resolve(),
    );
  };

  // Runs `take` after the current housing has been ended, and puts the old housing back if
  // `take` fails. Reports the original rejection - that is what the user needs to act on -
  // unless the restore fails too, which has to be said plainly because it needs a human.
  const replaceHousing = (take: () => Promise<any>, onDone: () => void) => {
    setErrMsg("");
    const prior = priorHousing();
    const hadHousing = !!prior.dwelling || !!prior.room || prior.beds.length > 0;
    let ended = false;

    endCurrentHousing()
      .then(() => {
        ended = true;
        return take();
      })
      .then(onDone)
      .catch((err: any) => {
        if (!ended || !hadHousing) return onError(err);
        return restoreHousing(prior)
          .then(() => {
            onError(err);
            onChanged();
          })
          .catch(() => {
            setErrMsg(
              `${caseName} could not be moved, and their previous housing could not be restored — they are currently unassigned. Please re-assign them.`,
            );
            window.scrollTo({ top: 0, behavior: "auto" });
            onChanged();
          });
      });
  };

  const assignWhole = (place: any, kind: string) => {
    replaceHousing(
      () =>
        kind === "dwelling" ? postDwellingOccupant({ property_id: place.id }, caseId) : postRoomOccupant({ room_id: place.id }, caseId),
      () => {
        afterChange(
          kind === "dwelling" ? "Dwelling Assigned" : "Room Assigned",
          `${caseName} — ${kind === "dwelling" ? place.name : roomLabel(place)}`,
        );
        onClose();
      },
    );
  };

  const assignBed = (bed: any) => {
    if (!currentPerson) return;
    const person = currentPerson;
    setErrMsg("");
    const body: any = { bed_id: bed.id };
    if (person.type === "guardian") {
      body.user_id = person.id;
    } else {
      body.member_id = person.id;
    }
    const placed = () => {
      // walk on to whoever still has nothing
      const next = residentialPeople.find((p: any) => personKey(p) !== personKey(person) && !bedByPerson.has(personKey(p)));
      setCurrentKey(next ? personKey(next) : "");
      setStep("mode");
      // a case usually lands in one room, so leave it open for the next person and only
      // fold it away once this assignment has taken its last free bed
      if (!bedsInRoom(bed.room_id).some((b: any) => !b.occupant_id && b.id !== bed.id)) {
        setOpenRoomId(null);
        setBedSearch("");
      }
      afterChange("Bed Assigned", `${personName(person)} — ${bedDisplayName(bed, rooms, facilities)}`);
    };

    // Coming out of a dwelling or a room unhouses the whole case to place one person, so that
    // has to be restorable if the bed does not take. Moving between beds needs no undo: the
    // API ends the person's previous bed itself as part of taking the new one.
    if (housedDwelling || housedRoom) {
      replaceHousing(() => postBedOccupant(body, caseId), placed);
      return;
    }

    const held = bedByPerson.get(personKey(person));
    (held ? patchBedOccupant(held.case_id, held.bed_occupant_id) : Promise.resolve())
      .then(() => postBedOccupant(body, caseId))
      .then(placed)
      .catch(onError);
  };

  const isPlaceCurrent = (place: any, kind: string) =>
    kind === "dwelling" ? housedDwelling && housedDwelling.id === place.id : housedRoom && housedRoom.id === place.id;

  const onClickAssignWhole = (place: any, kind: string) => {
    if (hasAnyHousing) {
      setPendingMove({ kind, place, label: kind === "dwelling" ? place.name : roomLabel(place) });
      return;
    }
    assignWhole(place, kind);
  };

  const onClickAssignBed = (bed: any) => {
    if (housedDwelling || housedRoom) {
      setPendingMove({ kind: "bed", place: bed, label: bedDisplayName(bed, rooms, facilities) });
      return;
    }
    assignBed(bed);
  };

  const onConfirmMove = () => {
    if (!pendingMove) return;
    if (pendingMove.kind === "bed") {
      assignBed(pendingMove.place);
    } else {
      assignWhole(pendingMove.place, pendingMove.kind);
    }
  };

  const onConfirmVacateWhole = () => {
    // The housing is read off props the parent replaces on every refresh, so it can be gone
    // by the time this confirm is answered - somebody else ending it, or our own reload
    // landing. Dereferencing it then throws synchronously, outside the promise chain, so the
    // catch below would never see it.
    if (!housedDwelling && !housedRoom) {
      setConfirmVacateWhole(false);
      setErrMsg("That assignment has already ended.");
      return;
    }
    setErrMsg("");
    const run = housedDwelling
      ? patchDwellingOccupant(caseId, housedDwelling.dwelling_occupant_id)
      : patchRoomOccupant(caseId, housedRoom.bed_room_occupant_id);
    run
      .then(() => {
        setMode("");
        setConfirmVacateWhole(false);
        afterChange(
          housedDwelling ? "Dwelling Assignment Ended" : "Room Assignment Ended",
          `${caseName} — ${housedDwelling ? housedDwelling.name : roomLabel(housedRoom)}`,
        );
      })
      .catch(onError);
  };

  const onConfirmVacateBed = (person: any) => {
    const held = bedByPerson.get(personKey(person));
    if (!held) return;
    setErrMsg("");
    patchBedOccupant(held.case_id, held.bed_occupant_id)
      .then(() => {
        setCurrentKey(personKey(person));
        setConfirmVacateKey("");
        afterChange("Bed Vacated", personName(person));
      })
      .catch(onError);
  };

  // ---- render ----

  const renderBed = (bed: any) => (
    <BedRow
      bed={bed}
      action={
        bed.occupant_id ? (
          <small className="text-muted align-self-center flex-shrink-0">Occupied</small>
        ) : (
          <Button
            size="sm"
            className="app-secondary-color button flex-shrink-0 flex-grow-0"
            disabled={!currentPerson || (movingToBed && pendingMove.place.id === bed.id)}
            onClick={() => onClickAssignBed(bed)}
          >
            Assign
          </Button>
        )
      }
    />
  );

  // Moving a case is a move, not a demolition - lead with where they are going and let the
  // detail line carry the fact that the old assignment closes itself out.
  const movingToBed = pendingMove?.kind === "bed";

  const moveQuestion = !pendingMove
    ? ""
    : movingToBed
      ? `Assign ${currentPerson ? personName(currentPerson) : caseName} to ${pendingMove.label}?`
      : `Move ${caseName} to ${pendingMove.label}?`;

  const moveDetail = !pendingMove
    ? ""
    : currentPlaceLabel
      ? movingToBed
        ? `This case is in ${currentPlaceLabel} now. That assignment ends, and everyone else on the case will need a bed.`
        : `This case is in ${currentPlaceLabel} now. That assignment ends automatically.`
      : `${pluralizePeople(placedPeople.length)} on this case ${placedPeople.length === 1 ? "has a bed" : "have beds"} now. Those end automatically.`;

  // A bed sits far down a long inventory, so its confirmation renders inside the room card the
  // user just clicked rather than at the top of a page they have already scrolled away from.
  const renderMoveConfirm = (inline?: boolean) =>
    pendingMove && (
      <Card className="bg-light" style={inline ? undefined : { maxWidth: 860 }}>
        <Card.Body className="p-3">
          <InlineConfirm
            question={moveQuestion}
            detail={moveDetail}
            confirmText={movingToBed ? "Assign Bed" : "Move Case"}
            confirmColor="secondary"
            onConfirm={onConfirmMove}
            onCancel={() => setPendingMove(null)}
          />
        </Card.Body>
      </Card>
    );

  const errorLine = (
    <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
      {errMsg}
    </p>
  );

  if (activeStep === "choose") {
    return (
      <div className="case-housing-flow">
        {errorLine}
        <PageTitle
          title={caseName}
          type="section"
          subTitleText={`${pluralizePeople(residentialPeople.length)} to house · choose how`}
          backButtonText={backLabel}
          onBackClick={onClose}
        />
        <Row className="g-3 mt-1" style={{ maxWidth: 860 }}>
          {[
            { key: "dwelling", label: "Dwelling", count: dwellingOptions.length, caption: "fit this case" },
            { key: "room", label: "Room", count: roomOptions.length, caption: "fit this case" },
            { key: "individual", label: "Bed", count: openBedCount, caption: "open beds" },
          ].map((choice: any) => (
            <Col xs={12} md={4} key={choice.key}>
              <Card
                className={choice.count > 0 ? "cursor-pointer h-100" : "h-100 bg-light opacity-75"}
                style={choice.count > 0 ? undefined : { cursor: "not-allowed" }}
                onClick={choice.count > 0 ? () => onPickMode(choice.key) : undefined}
              >
                <Card.Body className="p-3">
                  <div className="small fw-semibold app-header-text">{choice.label}</div>
                  <div className="small text-muted mt-1">
                    {choice.count > 0 ? `${choice.count} ${choice.caption}` : "Nothing available"}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div className="case-housing-flow">
      {errorLine}
      {activeMode !== "individual" && (
        <>
          <PageTitle
            title={`${modeName(activeMode)} Assignment`}
            type="section"
            subTitleText={`Vacant places with room for all ${residentialPeople.length}`}
            buttonTitle="Edit Housing Type"
            buttonColor="primary"
            onButtonClick={() => setStep("choose")}
            backButtonText={backLabel}
            onBackClick={onClose}
          />

          <Card className="bg-light mb-3 mt-2" style={{ maxWidth: 860 }}>
            <Stack direction="horizontal" gap={3} className="p-2 align-items-center flex-wrap">
              <span className="small fw-semibold app-header-text flex-grow-1">{caseName}</span>
              <span className="small text-muted">{pluralizePeople(residentialPeople.length)} to house</span>
            </Stack>
          </Card>
        </>
      )}

      {activeMode !== "individual" ? (
        <Stack gap={2} style={{ maxWidth: 860 }}>
          {(activeMode === "dwelling" ? dwellingOptions : roomOptions).length === 0 ? (
            <p className="text-muted small">Nothing vacant is big enough for {pluralizePeople(residentialPeople.length)}.</p>
          ) : (
            (activeMode === "dwelling" ? dwellingOptions : roomOptions).map((place: any) => {
              const isCurrent = isPlaceCurrent(place, activeMode);
              const counts =
                activeMode === "dwelling"
                  ? `${pluralizeCount(roomsOfProperty(place.id).length, "room")} · ${pluralizeCount(dwellingBedCount(place), "bed")}`
                  : pluralizeCount(bedsInRoom(place.id).length, "bed");
              const address = activeMode === "dwelling" ? propertyAddressLine(place) : "";
              const confirmingHere = !!pendingMove && !movingToBed && pendingMove.place.id === place.id;
              return (
                <React.Fragment key={place.id}>
                  {/* sits against the place it asks about, since the list can be long */}
                  {confirmingHere && renderMoveConfirm()}
                  <Card className="bg-light mb-0">
                    <Card.Body className="p-3">
                      <Stack direction="horizontal" gap={3} className="flex-wrap">
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="small fw-semibold">{activeMode === "dwelling" ? place.name : roomLabel(place)}</div>
                          {address && <div className="text-muted small">{address}</div>}
                          <div className="small">{counts}</div>
                        </div>
                        {!(isCurrent && confirmVacateWhole) && (
                          <Stack direction="horizontal" gap={2} className="align-items-center">
                            <span className={isCurrent ? "role-dot role-dot-orange" : "role-dot role-dot-green"}></span>
                            <span className="small">{isCurrent ? "Housed here" : "Vacant"}</span>
                          </Stack>
                        )}
                        {isCurrent && !confirmVacateWhole && (
                          <Button size="sm" className="app-danger-color button flex-shrink-0" onClick={() => setConfirmVacateWhole(true)}>
                            Vacate Case
                          </Button>
                        )}
                        {isCurrent && confirmVacateWhole && (
                          <InlineConfirm
                            question={`End ${caseName}'s stay here?`}
                            onConfirm={onConfirmVacateWhole}
                            onCancel={() => setConfirmVacateWhole(false)}
                          />
                        )}
                        {!isCurrent && (
                          <Button
                            size="sm"
                            className="app-secondary-color button flex-shrink-0"
                            disabled={!!pendingMove && !movingToBed && pendingMove.place.id === place.id}
                            onClick={() => onClickAssignWhole(place, activeMode)}
                          >
                            Assign
                          </Button>
                        )}
                      </Stack>
                    </Card.Body>
                  </Card>
                </React.Fragment>
              );
            })
          )}
        </Stack>
      ) : (
        <>
          {/* 1px so the observer has a box to watch - it crosses the pin line just before the block does */}
          <div ref={stickySentinel} style={{ height: 1 }}></div>
          <div
            className={showMenu ? "sticky-top bg-white card-note-menu-open-sticky pb-2" : "sticky-top bg-white card-note-sticky pb-2"}
            style={{ zIndex: 2 }}
          >
            <PageTitle
              title={`${modeName(activeMode)} Assignment`}
              type="section"
              buttonTitle="Edit Housing Type"
              buttonColor="primary"
              onButtonClick={() => setStep("choose")}
              backButtonText={backLabel}
              onBackClick={onClose}
            />

            {currentPerson && (
              <div className="mt-3" style={{ maxWidth: 860 }}>
                {openRoomId ? (
                  <SearchBar
                    key="beds"
                    cssClass=""
                    placeholder="Search beds"
                    searchText={bedSearch}
                    setSearchText={setBedSearch}
                    searchLogic={() => {}}
                  />
                ) : (
                  <SearchBar
                    key="rooms"
                    cssClass=""
                    placeholder="Search facilities and rooms"
                    searchText={invSearch}
                    setSearchText={setInvSearch}
                    searchLogic={() => {}}
                  />
                )}
              </div>
            )}

            <Card className="bg-light mt-3 mb-3" style={{ maxWidth: 860 }}>
              <Card.Body className="p-2">
                <Stack direction="horizontal" gap={3} className="align-items-baseline px-2 pb-2">
                  <span className="small text-muted flex-grow-1">Who needs a bed</span>
                  <span className="small text-muted">
                    {placedPeople.length} of {residentialPeople.length} placed
                  </span>
                </Stack>
                {rosterPeople.map((p: any) => {
                  const held = bedByPerson.get(personKey(p));
                  const isNow = !held && currentPerson && personKey(p) === personKey(currentPerson);
                  return (
                    <Stack
                      key={personKey(p)}
                      direction="horizontal"
                      gap={3}
                      className={isNow ? "align-items-center p-2 rounded bg-white" : "align-items-center p-2"}
                    >
                      {held ? (
                        <FontAwesomeIcon icon={faCheck} style={{ color: "#7d9158" }} />
                      ) : isNow ? (
                        <span className="role-dot role-dot-orange"></span>
                      ) : (
                        <span className="role-dot" style={{ background: "transparent", border: "1px solid #adb5bd" }}></span>
                      )}
                      <span className="small fw-semibold flex-shrink-0">{personName(p)}</span>
                      <span className={held ? "small text-muted flex-grow-1" : "small flex-grow-1"} style={{ minWidth: 0 }}>
                        {held ? bedDisplayName(held, rooms, facilities) : isNow ? "Choosing a bed now" : "Waiting"}
                      </span>
                      {held && confirmVacateKey === personKey(p) ? (
                        <InlineConfirm
                          question={`End ${personName(p)}'s stay here?`}
                          onConfirm={() => onConfirmVacateBed(p)}
                          onCancel={() => setConfirmVacateKey("")}
                        />
                      ) : held ? (
                        <Stack direction="horizontal" gap={2} className="flex-shrink-0">
                          <Button size="sm" className="app-primary-color-inverse button" onClick={() => setCurrentKey(personKey(p))}>
                            Move
                          </Button>
                          <div className="vr" />
                          <Button size="sm" className="app-danger-color button" onClick={() => setConfirmVacateKey(personKey(p))}>
                            Vacate
                          </Button>
                        </Stack>
                      ) : (
                        <Button
                          size="sm"
                          className="app-primary-color-inverse button flex-shrink-0"
                          onClick={() => setCurrentKey(isNow ? nextWaitingAfter(p) : personKey(p))}
                        >
                          {isNow ? "Skip" : "Assign"}
                        </Button>
                      )}
                    </Stack>
                  );
                })}
              </Card.Body>
            </Card>
          </div>

          {!movingToBed && renderMoveConfirm()}

          {currentPerson ? (
            <div style={{ maxWidth: 860 }} className="mt-3">
              {inventory.length === 0 ? (
                <p className="text-muted small">No rooms match this search.</p>
              ) : (
                inventory.map((entry: any, index: number) =>
                  entry.kind === "group" ? (
                    <h6 key={`g-${index}`} className="app-header-text mt-3 mb-2">
                      {entry.label}
                      {entry.note && <span className="small text-muted fw-normal ms-2">{entry.note}</span>}
                    </h6>
                  ) : (
                    <Card key={entry.room.id} className="mb-2">
                      <Card.Body className="px-3 py-3">
                        <Stack direction="horizontal" gap={3} className="flex-wrap align-items-center">
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="small fw-semibold">{roomLabel(entry.room)}</div>
                            <div className="text-muted small">
                              {pluralizeCount(entry.roomBeds.length, "bed")} · {entry.open.length} open
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <ManageBedsButton
                              open={openRoomId === entry.room.id}
                              bedCount={entry.roomBeds.length}
                              onToggle={() => {
                                setBedSearch("");
                                setOpenRoomId((current: any) => (current === entry.room.id ? null : entry.room.id));
                              }}
                            />
                          </div>
                        </Stack>
                        {openRoomId === entry.room.id && (
                          <RoomBedsPanel
                            beds={entry.roomBeds}
                            search={bedSearch}
                            setSearch={setBedSearch}
                            showSearch={false}
                            renderBed={renderBed}
                            // the confirmation belongs against the bed that raised it
                            renderBefore={(b: any) =>
                              movingToBed && pendingMove.place.id === b.id ? <Col xs={12}>{renderMoveConfirm(true)}</Col> : null
                            }
                          />
                        )}
                      </Card.Body>
                    </Card>
                  ),
                )
              )}
            </div>
          ) : (
            <Card className="bg-light" style={{ maxWidth: 860 }}>
              <Card.Body className="p-3">
                <Stack direction="horizontal" gap={3} className="align-items-center">
                  <FontAwesomeIcon icon={faCheck} style={{ color: "#7d9158" }} />
                  <span className="small fw-semibold">Everyone on this case has a bed</span>
                  <span className="small text-muted">Use Move above to change one.</span>
                </Stack>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default CaseHousingFlow;
